import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { Form } from "react-bootstrap";
import {
  LoginService,
  OnboardingService,
  OnboardingUserTypesEnum,
  PropertyManageTypesEnum,
  OpenAPI,
} from "@propertelligent/client-api";

import { TopBar } from "../../common/TopBar";
import AddButtonWithArrow from "../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import InputField from "../../../newComponents/form-fields/InputField";
import apiHelper from "../../../utils/apiHelper";
import { useError } from "../../../utils/context/ErrorContext";
import { useOnboardingContext } from "../../../utils/context/OnboardingContext";
import { MyContext } from "../../../utils/context/LoginContext";
import { URLS, USERS, USER_ROLES } from "../../../constants/constants";
import { UserLoginData } from "../../common/charts/Enums";

import "./inviteUser.css";
import EmailInput from "../../../newComponents/form-fields/EmailInput";
import PhoneInput from "../../../newComponents/form-fields/PhoneInput";
import { usePermissions } from "../../../hooks/usePermissions";

interface InviteUserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

const InviteUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orgUserInviteCode = params.get("ic");

  const { setIsLogin, setUserRole } = useContext(MyContext);
  const { refreshPermissions } = usePermissions();

  // Navigation history
  const [historyPageURL, setHistoryPageURL] = useState(
    window?.location?.pathname
  );

  const [loading, setLoading] = useState(false);

  const methods = useForm<InviteUserFormValues>({
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { handleSubmit, setValue, watch, setError } = methods;

  // Fetch Invite/User Info
  useEffect(() => {
    apiHelper(
      OnboardingService.getApiOnboardingOrgUserOrganizationHeaderInfo,
      { showNotification: false },
      orgUserInviteCode
    );

    // Get User Info (Prefill)
    apiHelper(
      OnboardingService.getApiOnboardingOrgUserUserInfo,
      { showNotification: false },
      orgUserInviteCode
    )
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.success;
          setValue("firstName", data.firstName || "");
          setValue("lastName", data.lastName || "");
          setValue("email", data.email || "");
          setValue("phoneNumber", data.phoneNumber || "");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [orgUserInviteCode, setValue]);

  const onSubmit = (data: InviteUserFormValues) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }

    setLoading(true);

    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      password: data.password,
    };

    apiHelper(
      LoginService.postApiLoginAcceptOrganizationUserInvite,
      {
        successMessage: "User Added Successfully",
        failureMessage: "Unable to Add User",
        showNotification: true,
      },
      orgUserInviteCode,
      payload
    )
      .then((res) => {
        if (res?.data?.success) {
          if (!res.data.success.isEmailVerified) {
            navigate(URLS.HOME);
          } else {
            sessionStorage.setItem(UserLoginData.token, res.data.success.token);
            OpenAPI.TOKEN = res.data.success.token;
            sessionStorage.setItem(
              UserLoginData.user_name,
              `${res.data.success.firstName} ${res.data.success.lastName}`
            );
            sessionStorage.setItem(
              UserLoginData.user_id.toString(),
              res.data.success.userId
            );
            sessionStorage.setItem(
              UserLoginData.organization_id,
              res.data.success.organizationUserInfo?.organizationId
            );
            sessionStorage.setItem(
              "organizationUserId",
              res.data.success.organizationUserInfo?.organizationUserId?.toString() ||
                ""
            );
            sessionStorage.setItem(
              UserLoginData.currency_symbol,
              res.data.success.currencySymbol
            );
            sessionStorage.setItem(
              UserLoginData.permissions,
              JSON.stringify(
                res.data.success.organizationUserInfo?.permissions || {}
              )
            );
            sessionStorage.setItem(
              UserLoginData.organization_user_role,
              res.data.success.organizationUserInfo?.organizationUserRoleId?.toString() || ""
            );
            refreshPermissions();

            sessionStorage.setItem(UserLoginData.tenant_id, res.data.success.tenantInfo?.tenantId);
            sessionStorage.setItem("userOnboarded", "true");

            // Mark user as onboarded on backend automatically for invited users
            const onboardingPayload = {
              userId: res.data.success.userId,
              OnboardingUserTypeId: OnboardingUserTypesEnum.PropertyManager, // Default to Property Manager for invited staff
              NumberOfUnitsManage: 1, // Represents "1-10" units range (Safe default for invited staff)
              IsUsingSoftware: true,
              propertyManageTypeId: PropertyManageTypesEnum.SingleMultiFamilies,
            };
            apiHelper(OnboardingService.putApiOnboardingUserOnboarding, { showNotification: false }, onboardingPayload);

            sessionStorage.setItem("userRoleId", res.data.success.userRoleId);
            const userRole = USER_ROLES[res.data.success.userRoleId];

            if (userRole === USERS.TENANT) {
              sessionStorage.setItem(
                "tenantInfo",
                JSON.stringify(res.data?.success?.tenantInfo || {})
              );
              sessionStorage.setItem(
                UserLoginData.user_id,
                res.data.success.tenantInfo?.unitId?.toString() || ""
              );
            }

            setIsLogin(true);
            setUserRole(userRole);
            if (userRole === USERS.USER) {
              if (!res.data.success.userOnboarded) {
                navigate(URLS.HOME);
              } else {
                navigate(
                  historyPageURL !== URLS.HOME ? historyPageURL : URLS.HOME
                );
              }
            }
          }
        }
      })
      .catch((err) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="invite-background-image" style={{ paddingBottom: "0px" }}>
      <TopBar haveAccount="" signInUp="" />

      <div className="invite-mainContainer invite-form-container-main">
        <div className="invite-rightSideFormContainer">
          <div className="invite-formContainer">
            <p className="invite-signInFormTitle top_header mb-4">
              Create an Account
            </p>

            <FormProvider {...methods}>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <InputField
                      name="firstName"
                      label="First Name"
                      placeholder="First Name"
                      required
                      requiredMessage="First Name is required"
                      validation={{
                        minLength: { value: 2, message: "First name must be at least 2 characters long" },
                        maxLength: { value: 20, message: "First name cannot exceed 20 characters" },
                      }}
                      onlyAlphabets
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="lastName"
                      label="Last Name"
                      placeholder="Last Name"
                      required
                      requiredMessage="Last Name is required"
                      validation={{
                        minLength: { value: 2, message: "Last name must be at least 2 characters long" },
                        maxLength: { value: 20, message: "Last name cannot exceed 20 characters" },
                      }}
                      onlyAlphabets
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <EmailInput
                      name="email"
                      label="Email Address"
                      required
                      requiredMessage="Email is required"
                      placeholder="Enter Email Address"
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <PhoneInput
                      name="phoneNumber"
                      label="Contact No."
                      placeholder="Contact No."
                      required
                      requiredMessage="Phone Number is required"
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="password"
                      label="Password"
                      placeholder="Enter Password"
                      type="password"
                      required
                      requiredMessage="Password is required"
                      allowSpecialChars
                      validation={{
                        minLength: {
                          value: 8,
                          message:
                            "Password must be at least 8 characters long",
                        },
                        maxLength: {
                          value: 20,
                          message:
                            "Password cannot exceed 20 characters",
                        },
                        pattern: {
                          value:
                            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,20}$/,
                          message:
                            "Password must contain at least one lowercase letter, one uppercase letter, one number, one special character, and be between 8 and 20 characters long",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="confirmPassword"
                      label="Confirm Password"
                      placeholder="Enter Confirm Password"
                      type="password"
                      required
                      requiredMessage="Confirm Password is required"
                      allowSpecialChars
                      validation={{
                        validate: (val: string) => {
                          if (watch("password") != val) {
                            return "Your passwords do no match";
                          }
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-center mt-4">
                  <AddButtonWithArrow
                    buttonname={"Get Started"}
                    type={"submit"}
                    className="invite-signUpButton sub_heading w-100"
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </Form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteUser;
